/**
 * 文档瑕疵自检工具
 * 对应 docs/招投标新变化.md「变化二：评审零容错」：AI 评标对微小瑕疵零容忍，
 * 资质有效期 / 错别字 / 页码错乱等都会被精准标记并触发预警/废标。
 *
 * 输入方式：
 *  - 粘贴文本：直接粘贴技术/商务部分正文；
 *  - 上传投标文件（docx/pdf/txt/md）：后端会自动抽取文本，并对文档内嵌图片做 OCR，
 *    使图片中的文字（扫描件证书、截图、带文字图表）也参与自查。
 *
 * 重文档（如整本扫描件）改为异步任务 + SSE 实时进度，避免同步请求 120s 超时。
 */
import { useRef, useState } from 'react';
import { getErrorMessage, submitDocFlawCheckFile, submitDocFlawCheckText, subscribeDocFlawProgress, pollDocFlawStatus, fetchDocFlawResult } from '../../services/api';
import {
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/outline';

interface TypoItem { snippet: string; issue: string; suggestion: string; reason: string; }
interface QualItem { keyword: string; cert?: string; date: string; status: string; snippet: string; }
interface PageItem { type: string; detail: string; snippet?: string; pages?: number[]; }

interface FlawResult {
  risk_level: 'low' | 'medium' | 'high';
  summary: string;
  typos: TypoItem[];
  qualifications: QualItem[];
  page_numbers: PageItem[];
}

const RISK_STYLE: Record<string, { label: string; cls: string }> = {
  high: { label: '高风险', cls: 'bg-red-100 text-red-700' },
  medium: { label: '中风险', cls: 'bg-amber-100 text-amber-700' },
  low: { label: '低风险', cls: 'bg-green-100 text-green-700' },
};

const QUAL_STATUS: Record<string, { label: string; cls: string }> = {
  expired: { label: '已过期', cls: 'bg-red-100 text-red-700' },
  near: { label: '临界', cls: 'bg-amber-100 text-amber-700' },
  ok: { label: '有效', cls: 'bg-green-100 text-green-700' },
};

export default function DocFlawCheck() {
  const [mode, setMode] = useState<'text' | 'file'>('text');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [referenceDate, setReferenceDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<FlawResult | null>(null);
  const [extractedChars, setExtractedChars] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');
  const idleTimer = useRef<number | null>(null);

  const handleCheck = async () => {
    const useFile = mode === 'file';
    if (useFile && !file) {
      setError('请先选择要自查的投标文件。');
      return;
    }
    if (!useFile && !text.trim()) {
      setError('请先粘贴需要自检的投标文本内容（技术或商务部分）。');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    setExtractedChars(null);
    setProgress('已提交，后台处理中…');

    const token = localStorage.getItem('auth_token') || '';
    let tid = '';
    try {
      if (useFile && file) {
        const data = await submitDocFlawCheckFile(file, referenceDate || undefined);
        tid = data.task_id;
      } else {
        const data = await submitDocFlawCheckText(text, referenceDate || undefined);
        tid = data.task_id;
      }
    } catch (e) {
      setError(getErrorMessage(e));
      setLoading(false);
      return;
    }

    const onDone = (payload: any) => {
      if (idleTimer.current) { clearTimeout(idleTimer.current); idleTimer.current = null; }
      let resultData = payload?.result;
      const finish = (res?: any) => {
        if (res?.result) resultData = res.result;
        if (resultData) {
          setResult(resultData);
          setExtractedChars(payload?.extracted_chars ?? res?.extracted_chars ?? null);
        }
        setLoading(false);
        setProgress('自检完成');
      };
      if (resultData) finish();
      else if (payload?.task_id) {
        fetchDocFlawResult(payload.task_id, token)
          .then(finish)
          .catch((err) => { setError(getErrorMessage(err)); setLoading(false); });
      } else {
        setLoading(false);
      }
    };

    const resetIdle = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = window.setTimeout(async () => {
        try {
          const s = await pollDocFlawStatus(tid, token);
          if (s?.status === 'done') onDone({ task_id: tid });
          else if (s?.status === 'error') { setError(s?.error || '后台任务失败'); setLoading(false); }
          else resetIdle();
        } catch {
          resetIdle();
        }
      }, 120000);
    };
    resetIdle();

    try {
      await subscribeDocFlawProgress(tid, token, (event: any) => {
        resetIdle();
        if (event.error || event.type === 'error') {
          setError(event.message || '后台任务失败');
          setLoading(false);
          return;
        }
        if (event.type === 'heartbeat') return;
        if (event.type === 'status_snapshot') {
          if (event.progress?.message) setProgress(event.progress.message);
          return;
        }
        if (event.type === 'phase' || event.type === 'progress') {
          if (event.message) setProgress(event.message);
          return;
        }
        if (event.type === 'done') {
          onDone(event);
        }
      });
    } catch (e) {
      // SSE 中断时依赖看门狗兜底轮询
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <MagnifyingGlassIcon className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-800">文档瑕疵自检</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        对应「变化二：评审零容错」。AI 评标对资质有效期、错别字、页码错乱等微小瑕疵零容忍。
        可粘贴文本，或上传投标文件（docx/pdf，内嵌图片会自动 OCR 提取文字后一并检查）。重文档走异步任务 + 实时进度，不会超时。
      </p>

      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setMode('text')}
          className={`px-3 py-1.5 text-sm rounded-md border ${
            mode === 'text' ? 'bg-blue-50 border-blue-400 text-blue-700' : 'border-gray-300 text-gray-600'
          }`}
        >
          粘贴文本
        </button>
        <button
          onClick={() => setMode('file')}
          className={`px-3 py-1.5 text-sm rounded-md border flex items-center gap-1 ${
            mode === 'file' ? 'bg-blue-50 border-blue-400 text-blue-700' : 'border-gray-300 text-gray-600'
          }`}
        >
          <DocumentArrowUpIcon className="w-4 h-4" />
          上传投标文件
        </button>
      </div>

      <div className="space-y-3">
        {mode === 'text' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              投标文本内容（技术或商务部分）
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={12}
              placeholder="在此粘贴需要自查的标书正文..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            />
            <div className="text-xs text-gray-400 mt-1">已输入 {text.length} 字</div>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              投标文件（支持 .docx / .pdf / .txt / .md）
            </label>
            <input
              type="file"
              accept=".docx,.pdf,.txt,.md,.markdown"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {file && (
              <div className="text-xs text-gray-500 mt-1">已选择：{file.name}（{Math.round(file.size / 1024)} KB）</div>
            )}
            <p className="text-xs text-gray-400 mt-1">文档内嵌图片（如扫描件证书、截图）会自动 OCR 提取文字后参与自查。</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            资质有效期参照日（可选）
          </label>
          <input
            type="date"
            value={referenceDate}
            onChange={(e) => setReferenceDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <span className="text-xs text-gray-400 ml-2">缺省按今天比对，建议填投标截止日</span>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleCheck}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {loading ? '自检中...' : '开始自检'}
          </button>
          {loading && (
            <span className="text-sm text-gray-400 flex items-center gap-2">
              <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
              {progress || '后台处理中…'}
            </span>
          )}
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}
      </div>

      {result && (
        <div className="mt-6 space-y-5">
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${RISK_STYLE[result.risk_level]?.cls || 'bg-gray-100 text-gray-600'}`}>
              {RISK_STYLE[result.risk_level]?.label || result.risk_level}
            </span>
            <span className="text-sm text-gray-700">{result.summary}</span>
          </div>
          {extractedChars != null && (
            <div className="text-xs text-gray-400">本次共提取 {extractedChars} 字（含图片 OCR 文字）参与检查</div>
          )}

          <section>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">错别字 / 语病（{result.typos.length}）</h3>
            {result.typos.length === 0 ? (
              <p className="text-sm text-gray-400">未检测到明显文字问题。</p>
            ) : (
              <ul className="space-y-2">
                {result.typos.map((t, i) => (
                  <li key={i} className="p-3 border border-gray-200 rounded-md">
                    <div className="text-sm text-gray-800">
                      <span className="inline-block px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs mr-2">{t.issue}</span>
                      <code className="text-gray-600">{t.snippet}</code>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">建议：{t.suggestion}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{t.reason}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">资质 / 证书有效期（{result.qualifications.length}）</h3>
            {result.qualifications.length === 0 ? (
              <p className="text-sm text-gray-400">未检测到资质/证书相关日期。</p>
            ) : (
              <ul className="space-y-2">
                {result.qualifications.map((q, i) => (
                  <li key={i} className="p-3 border border-gray-200 rounded-md">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${QUAL_STATUS[q.status]?.cls || 'bg-gray-100 text-gray-600'}`}>
                        {QUAL_STATUS[q.status]?.label || q.status}
                      </span>
                      <span className="text-sm text-gray-700">{q.cert || q.keyword}：{q.date}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">…{q.snippet}…</div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">页码检查（{result.page_numbers.length}）</h3>
            {result.page_numbers.length === 0 ? (
              <p className="text-sm text-gray-400">未检测到明显页码问题。</p>
            ) : (
              <ul className="space-y-2">
                {result.page_numbers.map((p, i) => (
                  <li key={i} className="p-3 border border-gray-200 rounded-md flex items-start gap-2">
                    {p.type === 'footer_mismatch' ? (
                      <ExclamationTriangleIcon className="w-4 h-4 text-red-500 mt-0.5" />
                    ) : (
                      <ClockIcon className="w-4 h-4 text-amber-500 mt-0.5" />
                    )}
                    <div className="text-sm text-gray-700">{p.detail}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
